import { useRef, useCallback } from 'react';
import { TextAreaField } from 'modules/templateEditor/common/components';
import { InAppContentDefinition } from 'modules/graphql/types';
import { FormField } from 'ui/formField';

import { constructFieldErrors, getIsEditMode, hasHandlebars } from '../../../common/utils';
import { useQueryParamContext } from '../../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../../context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from '../../constants';
import { useErrorValidationContext } from '../../../context/ErrorValidationContext';
import { constructErrorInlineText } from '../../../common/ErrorInlineText';
import { FieldNames } from '../../../common/types';
import { getRokuRestrictionText } from '../../utils';

export const RokuBody = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { ottPayload } = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();

  const bodyHasHandlebars = hasHandlebars(ottPayload?.dialogText);

  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const mediumDataValue = useCallback(() => {
    if (ottPayload?.dialogText) return ottPayload.dialogText;
    if (!isEditModeVisible) return SETTINGS_DISPLAY_CONTENT.roku.title.unset;
    return '';
  }, [isEditModeVisible, ottPayload?.dialogText]);

  const { hasError: hasPushMessageError, hasWarning: hasPushMessageWarning } = constructFieldErrors(
    {
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.ROKU_BODY,
    },
  );

  const bodyPushMessageErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasPushMessageError,
    hasWarning: hasPushMessageWarning,
  });

  return (
    <FormField
      isHorizontalLayout
      hasError={hasPushMessageError}
      hasWarning={hasPushMessageWarning}
      errorText={bodyPushMessageErrorText}
      labelText={SETTINGS_DISPLAY_CONTENT.roku.body.label}
      restrictionText={getRokuRestrictionText(ottPayload?.dialogText, isEditModeVisible)}
      data-test="form-field-roku-body"
    >
      <TextAreaField
        hasError={hasPushMessageError}
        mediumRef={bodyRef}
        mediumDataKey={'ottPayload.dialogText'}
        mediumDataValue={mediumDataValue()}
        onChangeHandler={(body) =>
          setCurrentMediumField('ottPayload', { ...ottPayload, dialogText: body })
        }
        maxCharacters={bodyHasHandlebars ? undefined : 100}
      />
    </FormField>
  );
};
